import React, { useEffect, useCallback, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import CardColor from '../assets/images/icons/card-icon.png'
import Button from '../components/ui/button'
import { useLanguage } from '../context/LanguageContext'
import { useOffers } from '../context/OffersContext'
import { useTenant } from '../context/TenantContext'
import { useClient } from '../context/ClientContext'
import { useNPS } from '../hooks/useNPS'
import FeedbackModal from '../components/ui/FeedbackModal'
import { ChevronRight, MapPin } from 'lucide-react'
import onlineTagIcon from '../assets/images/icons/onlinetag.png'
import apiClient from '../services/api.service'
import OfferLocationsModal from '../components/ui/modal-offer-locations'
import { getImageByType } from '../utils/imageUtils'
import {
  formatDate as formatDateUtil,
  formatValidUntil,
} from '../utils/dateFormatter'
import { HomeIcon } from '../components/icons'
import DOMPurify from 'dompurify'
import {
  ShimmerCircularImage,
  ShimmerText,
  ShimmerThumbnail,
  ShimmerTitle,
} from 'react-shimmer-effects'

// Utility function to parse special link format: <<[link-text:url]>>
const parseSpecialLinks = (htmlContent) => {
  if (!htmlContent) return htmlContent

  // Match both raw and HTML-encoded versions of the pattern
  // Pattern: <<[ text : url ]>> or &lt;&lt;[ text : url ]&gt;&gt;
  // Handles optional spaces around text, colon, and URL
  // Uses \s* to match optional whitespace (spaces, tabs, etc.)
  const pattern =
    /(?:&lt;&lt;|<<)\[\s*([^:]+?)\s*:\s*(https?:\/\/[^\]]+?)\s*\](?:&gt;&gt;|>>)/g

  return htmlContent.replace(pattern, (match, linkText, url) => {
    // Trim any remaining whitespace from text and URL
    const cleanText = linkText.trim()
    const cleanUrl = url.trim()

    // Return a proper anchor tag with blue color styling
    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" style="color: #0000FF; text-decoration: underline;">${cleanText}</a>`
  })
}

const OfferDetailsSkeleton = () => {
  const Section = () => (
    <div className="space-y-2">
      <div className="w-40">
        <ShimmerTitle line={1} gap={10} variant="primary" />
      </div>
      <ShimmerText line={3} gap={10} />
    </div>
  )

  const OtherOfferCard = ({ idx }) => (
    <div
      key={idx}
      className="bg-white rounded-[10px] shadow-md overflow-hidden w-[180px] h-[257px] flex-shrink-0"
    >
      <ShimmerThumbnail height={117} />
      <div className="px-2 py-2">
        <div className="flex items-center">
          <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center overflow-hidden">
            <ShimmerCircularImage size={38} />
          </div>
          <div className="ml-2 w-24">
            <ShimmerTitle line={1} gap={10} variant="secondary" />
          </div>
        </div>
        <div className="mt-3">
          <ShimmerText line={2} gap={10} />
        </div>
        <div className="mt-3 w-24">
          <ShimmerTitle line={1} gap={10} variant="secondary" />
        </div>
      </div>
    </div>
  )

  return (
    <div className="offer-details-page">
      {/* Banner */}
      <div className="main-banner">
        <div className="w-full overflow-hidden">
          <div className="md:hidden">
            <ShimmerThumbnail height={181} rounded />
          </div>
          <div className="hidden md:block">
            <ShimmerThumbnail height={420} rounded />
          </div>
        </div>
      </div>

      <div className="w-full px-3">
        {/* Feature header */}
        <div className="px-2 py-2">
          <div className="flex items-center">
            <div className="w-[62px] h-[62px] rounded-full flex items-center justify-center overflow-hidden">
              <ShimmerCircularImage size={62} />
            </div>
            <div className="ml-2 w-28">
              <ShimmerTitle line={1} gap={10} variant="secondary" />
            </div>
          </div>
          <div className="mt-3">
            <ShimmerText line={2} gap={10} />
          </div>
        </div>

        <div className="px-2 py-2 text-left space-y-6">
          <Section />
          <Section />

          {/* Eligible cards image row */}
          <div className="space-y-2">
            <div className="w-32">
              <ShimmerTitle line={1} gap={10} variant="primary" />
            </div>
            <div className="flex gap-3">
              <ShimmerThumbnail width={120} height={56} rounded />
              <ShimmerThumbnail width={120} height={56} rounded />
              <ShimmerThumbnail width={120} height={56} rounded />
            </div>
            <ShimmerText line={2} gap={10} />
          </div>

          {/* Participating stores */}
          <div className="space-y-3">
            <div className="w-56 pt-6">
              <ShimmerTitle line={1} gap={10} variant="primary" />
            </div>
            <ShimmerThumbnail height={200} rounded />
            <ShimmerText line={1} gap={10} />
          </div>
        </div>

        {/* Other offers slider */}
        <div className="feature-slider px-3 pt-6">
          <div className="w-28 mt-4 mb-2">
            <ShimmerTitle line={1} gap={10} variant="primary" />
          </div>
          <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <OtherOfferCard key={i} idx={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const OfferDetails = () => {
  const { t, languageCode, languageCodeDate } = useLanguage()
  const { offerId } = useParams()
  const navigate = useNavigate()
  const { tenant, isLoading: tenantLoading } = useTenant()
  const { offerDetails, loadingOfferDetails, offers, featuredOffers, fetchOfferById } =
    useOffers()
  const { tenanturlname } = useParams()
  const { isAuthenticated, client } = useClient()
  const [offerLocations, setOfferLocations] = useState([])
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [featuredData, setFeaturedData] = useState([])
  const [activationLoading, setActivationLoading] = useState(false)
  const [selectedCardNumber, setSelectedCardNumber] = useState('')
  const [isActivated, setIsActivated] = useState(false)
  const [localActivationExpiry, setLocalActivationExpiry] = useState(null)
  const [showActivationSuccessModal, setShowActivationSuccessModal] = useState(false)

  // NPS tracking - use ref to persist across offer changes
  const { checkEligibility } = useNPS()
  const [showNPS, setShowNPS] = useState(false)
  const [npsChecked, setNpsChecked] = useState(false)
  const viewedOffersRef = React.useRef(new Set())

  // Format date helper (wrapper for utility function)
  const formatDate = useCallback(
    (dateString, options = {}) => {
      const { includeTime = false, timeZone } = options
      return formatDateUtil(dateString, languageCodeDate, {
        includeTime,
        timeZone,
      })
    },
    [languageCodeDate],
  )

  // Helper function to get image URL (handles both full URLs and CDN paths)
  const getImageUrl = useCallback(
    (imagePath) => {
      if (!imagePath) return null
      // If it's already a full URL, return it
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath
      }
      // If it's a relative path, construct CDN URL
      const cdnBase = tenant?.cdnBase
      if (cdnBase) {
        return `${cdnBase}/${imagePath}`
      }
      return null
    },
    [tenant?.cdnBase],
  )

  // Transform featured offers (from context) - uses card-img
  const transformFeaturedOffer = useCallback(
    (offer) => {
      const primaryMerchant = offer.targetMerchants?.[0]
      // Get card-img from images array (web or mobile based on device)
      const cardImage = getImageByType(offer.images, 'card-img')
      const imageUrl =
        cardImage ||
        (offer.images && offer.images.length > 0
          ? getImageUrl(offer.images[0]) ||
          require('../assets/images/backgrounds/featured-banner-1.png')
          : primaryMerchant?.image
            ? getImageUrl(primaryMerchant.image) ||
            require('../assets/images/backgrounds/featured-banner-1.png')
            : require('../assets/images/backgrounds/featured-banner-1.png'))

      const iconUrl =
        offer.offerLogo && offer.offerLogo.trim()
          ? getImageUrl(offer.offerLogo) ||
          require('../assets/images/icons/featured-logo-1.png')
          : primaryMerchant?.image
            ? getImageUrl(primaryMerchant.image) ||
            require('../assets/images/icons/featured-logo-1.png')
            : require('../assets/images/icons/featured-logo-1.png')

      const title = offer.title || 'Offer'
      const merchantName =
        offer.merchantDisplayName ||
        primaryMerchant?.name ||
        ''

      return {
        id: offer.id,
        image: imageUrl,
        icon: iconUrl,
        title: title,
        description: offer.description || offer.title || '',
        validUntil: formatValidUntil(
          offer.validToUTC || offer.validTo,
          languageCode,
          t,
          { includeTime: false, timeZone: offer.timezone || undefined },
        ),
        merchantName: merchantName,
      }
    },
    [languageCode, t, getImageUrl],
  )

  // Add CSS for HTML content styling
  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `
      .offer-description-content {
        line-height: 1.6;
      }
      .offer-description-content p {
        margin: 0.5rem 0;
      }
      .offer-description-content p:first-child {
        margin-top: 0;
      }
      .offer-description-content p:last-child {
        margin-bottom: 0;
      }
      .offer-description-content p:empty {
        display: none;
      }
      .offer-description-content strong,
      .offer-description-content b {
        font-weight: 700;
      }
      .offer-description-content em,
      .offer-description-content i {
        font-style: italic;
      }
      .offer-description-content ul,
      .offer-description-content ol {
        margin: 0.5rem 0 !important;
        padding-left: 1.5rem !important;
        list-style-position: outside !important;
        display: block !important;
      }
      .offer-description-content ul {
        list-style-type: disc !important;
      }
      .offer-description-content ol {
        list-style-type: decimal !important;
      }
      .offer-description-content li {
        margin: 0.25rem 0 !important;
        display: list-item !important;
        list-style-position: outside !important;
        padding-left: 0.25rem !important;
      }
      .offer-description-content ul li {
        list-style-type: disc !important;
      }
      .offer-description-content ol li {
        list-style-type: decimal !important;
      }
      .offer-description-content a {
        color: #0000FF;
        text-decoration: underline;
      }
      .offer-description-content br {
        line-height: 1.6;
      }
    `
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
    }
  }, [])

  // Track offer views and check NPS eligibility based on tenant config
  useEffect(() => {
    if (offerId && isAuthenticated && !npsChecked) {
      // Get offer_engagement config from tenant
      const npsConfig = tenant?.tenantmasterconfig?.nps_config
      if (
        !npsConfig?.enabled ||
        !npsConfig?.triggers?.offer_engagement?.enabled
      ) {
        return
      }

      const triggerConfig = npsConfig.triggers.offer_engagement
      const requiredCount = triggerConfig.engagement_count || 2

      // Track this offer view
      if (!viewedOffersRef.current.has(offerId)) {
        viewedOffersRef.current.add(offerId)
        const currentCount = viewedOffersRef.current.size

        if (currentCount >= requiredCount) {
          setNpsChecked(true)
          const checkNPS = async () => {
            try {
              const result = await checkEligibility(
                'offer_engagement',
              )
              if (result?.eligible) {
                setShowNPS(true)
              }
            } catch (error) {
              console.error(
                'NPS offer_engagement: eligibility check failed:',
                error,
              )
            }
          }
          checkNPS()
        }
      }
    }
  }, [
    offerId,
    isAuthenticated,
    npsChecked,
    tenant?.tenantmasterconfig,
    checkEligibility,
  ])

  // Fetch offer details when offerId changes
  useEffect(() => {
    if (offerId) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: 'cta_click', ctaName: 'offer_view', offerId: String(offerId) })
      fetchOfferById(offerId)
    }
  }, [offerId, fetchOfferById])

  // Transform other offers from context: featured first, then regular offers.
  useEffect(() => {
    const combinedOffers = [...(featuredOffers || []), ...(offers || [])]

    if (combinedOffers.length === 0) {
      setFeaturedData([])
      return
    }

    const seenOfferIds = new Set()
    const currentOfferId = String(offerId)
    const otherOffers = combinedOffers
      .filter((offer) => {
        if (!offer?.id) return false

        const offerIdString = String(offer.id)
        if (offerIdString === currentOfferId || seenOfferIds.has(offerIdString)) {
          return false
        }

        seenOfferIds.add(offerIdString)
        return true
      })
      .slice(0, 4)
      .map(transformFeaturedOffer)

    setFeaturedData(otherOffers)
  }, [featuredOffers, offers, offerId, transformFeaturedOffer])

  // Fetch store locations when offerId and tenant are available
  useEffect(() => {
    const fetchLocations = async () => {
      if (!offerId || !tenant?.tenantid) return

      try {
        setLoadingLocations(true)
        const response = await apiClient.get('/api/offers/getofferlocations', {
          params: {
            tenantid: tenant.tenantid,
            offerid: offerId,
          },
        })

        const locationsData = response || []
        // Parse coordinates and filter out locations without names
        const validLocations = locationsData
          .filter((loc) => loc.name)
          .map((loc) => ({
            ...loc,
            longitude: parseFloat(loc.longitude),
            latitude: parseFloat(loc.latitude),
          }))

        setOfferLocations(validLocations)
      } catch (error) {
        console.error('Error fetching offer locations:', error)
        setOfferLocations([])
      } finally {
        setLoadingLocations(false)
      }
    }

    fetchLocations()
  }, [offerId, tenant?.tenantid])

  // Handle featured offer click
  const handleFeaturedOfferClick = useCallback(
    (id) => {
      if (tenanturlname && id) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'cta_click', ctaName: 'featured_offer_click', offerId: String(id) })
        navigate(`/${tenanturlname}/offer/${id}`)
      }
    },
    [navigate, tenanturlname],
  )

  // Handle home button click
  const handleHomeClick = useCallback(() => {
    if (tenanturlname) {
      navigate(`/${tenanturlname}`)
    }
  }, [navigate, tenanturlname])

  // Handle sign in link click
  const handleSignInClick = useCallback(() => {
    if (tenanturlname) {
      navigate(`/${tenanturlname}/signin`)
    }
  }, [navigate, tenanturlname])

  // Handle sign up button click
  const handleSignUpClick = useCallback(() => {
    if (tenanturlname) {
      navigate(`/${tenanturlname}/create-account`)
    }
  }, [navigate, tenanturlname])

  const handleActivateOffer = useCallback(async () => {
    if (!isAuthenticated || !client) return
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: 'cta_click', ctaName: 'offer_activate', offerId: String(offerId) })
    try {
      setActivationLoading(true)
      await apiClient.post('/api/offers/offerpostattribution', {
        externaluserid: client.externalUserId,
        offerid: offerId,
        action: 'ENROLL',
        tenantid: client.tenantId,
        clientid: client.clientId,
        cardnumber: selectedCardNumber || undefined,
      })
      setIsActivated(true)
      const activationPeriod = tenant?.tenantmasterconfig?.offer_activation?.activation_period || 0
      if (activationPeriod > 0) {
        setLocalActivationExpiry(new Date(Date.now() + activationPeriod * 60000).toISOString())
      }
      setShowActivationSuccessModal(true)
    } catch (error) {
      console.error('Error activating offer:', error)
    } finally {
      setActivationLoading(false)
    }
  }, [isAuthenticated, client, offerId, selectedCardNumber, tenant])
  // Avoid showing translation key-paths during initial tenant/language bootstrap.
  const translationsReady = !!tenant?.tenantlanguagecontent?.v3

  // Offer detail section visibility flags (default on if absent)
  const offerDetailConfig = tenant?.tenantmasterconfig?.offer_detail
  const showEligibleCards = offerDetailConfig?.eligiblecards?.show !== false
  const showParticipatingStores =
    offerDetailConfig?.participatingstores?.show !== false
  const showTnc = offerDetailConfig?.tnc?.show !== false
  const showSalesChannel =
    tenant?.tenantmasterconfig?.offer_card?.show_sales_channel === true
  const merchantLogoFill =
    tenant?.tenantmasterconfig?.offer_card?.merchant_logo_fill === true

  // Show shimmer loading state (no common Loader)
  if (tenantLoading || !translationsReady || loadingOfferDetails) {
    return <OfferDetailsSkeleton />
  }

  // Show error state if no offer details
  if (!offerDetails) {
    return (
      <div className="offer-details-page flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-theme-secondary mb-4">
            {t('offerDetails.offerNotFoundNew')}
          </div>
          <Button variant="primary" onClick={handleHomeClick}>
            {t('offerDetails.goHomeNew')}
          </Button>
        </div>
      </div>
    )
  }

  // Extract offer data
  const primaryMerchant = offerDetails.targetMerchants?.[0]
  // Get detail-img from images array (web or mobile based on device) for banner
  const detailImage = getImageByType(offerDetails.images, 'detail-img')
  const offerImage =
    detailImage ||
    (offerDetails.images && offerDetails.images.length > 0
      ? getImageUrl(offerDetails.images[0]) ||
      require('../assets/images/backgrounds/offer-details-banner-1.png')
      : primaryMerchant?.image
        ? getImageUrl(primaryMerchant.image) ||
        require('../assets/images/backgrounds/offer-details-banner-1.png')
        : require('../assets/images/backgrounds/offer-details-banner-1.png'))

  // Get store-img for participating stores section
  const storeImage = getImageByType(offerDetails.images, 'store-img')

  // Get eligible card image through the same shared image resolver used for
  // detail/store/card images. Current backend tags use eligible-card-web/mobile;
  // older payloads may still use eligible-card-img-web/mobile.
  const eligibleCardImage =
    getImageByType(offerDetails.images, 'eligible-card') ||
    getImageByType(offerDetails.images, 'eligible-card-img')

  const offerIcon =
    offerDetails.offerLogo && offerDetails.offerLogo.trim()
      ? getImageUrl(offerDetails.offerLogo) ||
      require('../assets/images/icons/offer-icon-1.png')
      : primaryMerchant?.image
        ? getImageUrl(primaryMerchant.image) ||
        require('../assets/images/icons/offer-icon-1.png')
        : require('../assets/images/icons/offer-icon-1.png')

  const offerTitle = offerDetails.title || 'Offer'
  const merchantName =
    offerDetails.merchantDisplayName ||
    primaryMerchant?.name ||
    ''

  const offerDescription =
    offerDetails.description ||
    offerDetails.termsAndConditions ||
    offerDetails.title ||
    ''
  const eligibleCardsContent = offerDetails.eligibleCards || ''
  const participatingStoresContent = offerDetails.participatingMerchants || ''
  const separator = languageCodeDate === 'ja-JP' ? ' ～ ' : ' – '
  const offerTimeZone = offerDetails.timezone || undefined
  const campaignPeriod =
    offerDetails.validFromUTC && offerDetails.validToUTC
      ? `${formatDate(offerDetails.validFromUTC, { timeZone: offerTimeZone })}${separator}${formatDate(offerDetails.validToUTC, { timeZone: offerTimeZone })}`
      : offerDetails.validFromUTC
        ? languageCodeDate === 'ja-JP'
          ? `${formatDate(offerDetails.validFromUTC, { timeZone: offerTimeZone })}から有効`
          : `${t('offerDetails.from')} ${formatDate(offerDetails.validFromUTC, { timeZone: offerTimeZone })}`
        : offerDetails.validToUTC
          ? `${t('offerDetails.until')} ${formatDate(offerDetails.validToUTC, { timeZone: offerTimeZone })}`
          : ''

  return (
    <>
      <div className="offer-details-page lg:mt-8">
        {/* BANNER - START */}
        <div className="main-banner lg:grid lg:grid-cols-2 lg:gap-3">
          <div className="w-full overflow-hidden">
            <img
              src={offerImage}
              alt={t('common.bannerAlt')}
              className="w-full md:h-[420px] object-cover"
              loading="lazy"
              onError={(e) => {
                e.target.src = require('../assets/images/backgrounds/offer-details-banner-1.png')
              }}
            />
          </div>

          <div>
            {/* Feature - START */}
            <div className="px-2 py-2">
              <div className="flex items-center">
                <div className={`w-[62px] h-[62px] border-2 border-theme-secondary-foreground rounded-full flex items-center justify-center ${merchantLogoFill ? 'overflow-hidden' : ''}`}>
                  <img
                    src={offerIcon}
                    alt={merchantName}
                    className={merchantLogoFill ? 'w-full h-full object-cover' : 'w-8 h-8 object-contain'}
                    onError={(e) => {
                      e.target.src = require('../assets/images/icons/offer-icon-1.png')
                    }}
                  />
                </div>
                <div className="text-sm font-bold text-theme-secondary ml-3">
                  {merchantName}
                </div>
              </div>

              <p className="mt-3 font-extrabold text-xl text-theme-secondary text-left line-clamp-3">
                {offerTitle}
              </p>
            </div>
            {/* Feature - END */}

            <div className="px-2 py-2 text-left space-y-6">
              {/* Campaign Overview Section */}
              {offerDescription && (
                <div className="">
                  <h2 className="text-xs font-extrabold text-theme-primary mb-1">
                    {t('offerDetails.campaignOverview') || 'Campaign Overview'}
                  </h2>
                  <div
                    className="text-xs font-normal text-theme-secondary mb-1 offer-description-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(offerDescription, {
                        ALLOWED_TAGS: [
                          'p',
                          'br',
                          'strong',
                          'b',
                          'em',
                          'i',
                          'u',
                          'ul',
                          'ol',
                          'li',
                          'div',
                          'span',
                          'a',
                          'h1',
                          'h2',
                          'h3',
                          'h4',
                          'h5',
                          'h6',
                        ],
                        ALLOWED_ATTR: ['href', 'target', 'rel'],
                        ALLOW_DATA_ATTR: false,
                        ALLOW_UNKNOWN_PROTOCOLS: false,
                        FORBID_TAGS: [
                          'script',
                          'iframe',
                          'object',
                          'embed',
                          'form',
                          'input',
                          'button',
                        ],
                        FORBID_ATTR: [
                          'onerror',
                          'onload',
                          'onclick',
                          'onmouseover',
                          'style',
                        ],
                      }),
                    }}
                  />
                </div>
              )}

              {/* Sales Channel Section */}
              {showSalesChannel && offerDetails.salesChannel && (
                <div className="flex items-center gap-1">
                  <img src={onlineTagIcon} alt="" className="w-[14px] h-[14px]" />
                  <span className="text-[11px] font-semibold text-theme-primary uppercase tracking-wide">
                    {offerDetails.salesChannel === 'ALL'
                      ? t('offerDetails.salesChannelAll')
                      : offerDetails.salesChannel === 'E-COMMERCE'
                        ? t('offerDetails.salesChannelOnline')
                        : t('offerDetails.salesChannelInStore')}
                  </span>
                </div>
              )}

              {/* Campaign Period Section */}
              {campaignPeriod && (
                <div className="">
                  <h2 className="text-xs font-extrabold text-theme-primary mb-1">
                    {t('offerDetails.campaignPeriod') || 'Campaign Period'}
                  </h2>
                  <p className="text-[10px] font-normal text-theme-secondary">
                    {campaignPeriod}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* BANNER - END */}

        <div className="w-full lg:px-0 px-3 lg:pt-10 pt-5 text-left space-y-6">
          <div>
            {/* Eligible Cards Section */}
            {showEligibleCards && (
              <div className="">
                <h2 className="text-xs font-extrabold text-theme-primary mb-1">
                  {t('offerDetails.eligibleCards') || 'Eligible Cards'}
                </h2>
                <div className="flex gap-3 mb-4">
                  {eligibleCardImage && (
                  <img
                    src={eligibleCardImage}
                    alt={t('offerDetails.eligibleCards') || 'Eligible Cards'}
                    className="-ml-[5px] max-w-full h-auto object-contain"
                    loading="lazy"
                  />
                  )}
                </div>
                {eligibleCardsContent !== '' ? (
                  <div
                    className="text-xs font-normal text-theme-secondary mb-1 offer-description-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        parseSpecialLinks(eligibleCardsContent),
                        {
                          ALLOWED_TAGS: [
                            'p',
                            'br',
                            'strong',
                            'b',
                            'em',
                            'i',
                            'u',
                            'ul',
                            'ol',
                            'li',
                            'div',
                            'span',
                            'a',
                            'h1',
                            'h2',
                            'h3',
                            'h4',
                            'h5',
                            'h6',
                          ],
                          ALLOWED_ATTR: ['href', 'target', 'rel'],
                          ALLOW_DATA_ATTR: false,
                          ALLOW_UNKNOWN_PROTOCOLS: false,
                          FORBID_TAGS: [
                            'script',
                            'iframe',
                            'object',
                            'embed',
                            'form',
                            'input',
                            'button',
                          ],
                          FORBID_ATTR: [
                            'onerror',
                            'onload',
                            'onclick',
                            'onmouseover',
                            'style',
                          ],
                        },
                      ),
                    }}
                  />
                ) : (
                  <div>
                    <p className="text-[10px] font-normal text-theme-secondary mb-1">
                      {t('offerDetails.eligibleCardsNote1') ||
                        'Only cards issued in Japan are eligible. Some cards may be excluded—check the campaign terms. Payments via other methods like iD or QUICPay are not eligible.'}
                      <Link
                        to={`https://www.visa.co.jp/about-visa/promotions/visapromo26feb-mobile.html`}
                        className="text-theme-primary underline"
                      >
                        {t('offerDetails.eligibleCardsNotelinkText')}
                      </Link>
                      {t('offerDetails.eligibleCardsNote3')}
                    </p>
                    <p className="text-[10px] font-normal text-theme-secondary mb-1">
                      {t('offerDetails.eligibleCardsNote2') ||
                        '※Mobile order/online payments are excluded—only in-store register payments qualify.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Participating Stores Section */}
            {showParticipatingStores && (
              <div className="space-y-3">
                <h2 className="text-xs font-extrabold text-theme-primary mb-1 pt-6">{t('offerDetails.participatingStores') || 'Participating Stores (Physical Locations)'}</h2>

                {storeImage ?


                  <div className="">
                    <img
                      src={storeImage || require('../assets/images/backgrounds/offer-details-img-1.png')}
                      alt={t('offerDetails.participatingStores') || 'Participating Stores'}
                      className="w-full lg:w-1/3 h-auto object-contain"
                      loading="lazy"
                      onError={(e) => {
                        //e.target.src = require('../assets/images/backgrounds/offer-details-img-1.png');
                      }}
                    />
                  </div>


                  : ""}
                {participatingStoresContent !== "" ?
                  (
                    <div className="text-xs font-normal text-theme-secondary mb-1 offer-description-content" dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(parseSpecialLinks(participatingStoresContent), {
                        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'div', 'span', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                        ALLOWED_ATTR: ['href', 'target', 'rel'],
                        ALLOW_DATA_ATTR: false,
                        ALLOW_UNKNOWN_PROTOCOLS: false,
                        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
                        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style'],
                      })
                    }}
                    />

                  ) : (
                    <p className="text-[10px] font-normal text-theme-secondary mt-3">
                      {t('offerDetails.storesNote')?.replace('{eligibleStores}', '')?.replace('{storeList}', '') ||
                        'Note: Some stores may not be eligible for the campaign.'}
                    </p>

                  )}

                {/* View Store Locations Button */}
                {offerLocations.length > 0 && (
                  <div className="mt-4">
                    <Button
                      variant="secondary"
                      size="default"
                      type="button"
                      onClick={() => {
                        window.dataLayer = window.dataLayer || []
                        window.dataLayer.push({ event: 'cta_click', ctaName: 'store_location_view', offerId: String(offerId), locationCount: offerLocations.length })
                        setIsLocationModalOpen(true)
                      }}
                      className="w-full"
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {t('offerLocations.viewButtonWithCount')?.replace(
                            '{count}',
                            offerLocations.length,
                          ) || `View ${offerLocations.length} Store Locations`}
                        </span>
                      </div>
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Terms & Conditions Section */}
            {showTnc && offerDetails.termsAndConditions && (
              <div className="pt-6">
                <h2 className="text-xs font-extrabold text-theme-primary mb-1">
                  {t('offerDetails.termsAndConditions') || 'Terms & Conditions'}
                </h2>
                <div
                  className="text-[10px] font-normal text-theme-secondary offer-description-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      offerDetails.termsAndConditions,
                      {
                        ALLOWED_TAGS: [
                          'p',
                          'br',
                          'strong',
                          'b',
                          'em',
                          'i',
                          'u',
                          'ul',
                          'ol',
                          'li',
                          'div',
                          'span',
                          'a',
                          'h1',
                          'h2',
                          'h3',
                          'h4',
                          'h5',
                          'h6',
                        ],
                        ALLOWED_ATTR: ['href', 'target', 'rel'],
                        ALLOW_DATA_ATTR: false,
                        ALLOW_UNKNOWN_PROTOCOLS: false,
                        FORBID_TAGS: [
                          'script',
                          'iframe',
                          'object',
                          'embed',
                          'form',
                          'input',
                          'button',
                        ],
                        FORBID_ATTR: [
                          'onerror',
                          'onload',
                          'onclick',
                          'onmouseover',
                          'style',
                        ],
                      },
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {/* 3 Steps- START (Only visible to guest users) */}
          {!isAuthenticated && (
            <div className="text-center mt-12 lg:pt-20">
              <h2 className="font-bold text-sm text-theme-secondary">
                {t('homePage.signUpTitle')}
              </h2>
              <p className="mt-1 font-normal text-xs text-theme-secondary">
                {t('homePage.signUpSubtitle')}{' '}
                {tenant?.tenantmasterconfig?.howtoregisterpage?.show !==
                  false && (
                    <Link
                      to={`/${tenanturlname}/about`}
                      className="text-theme-primary underline"
                    >
                      {t('homePage.easySteps')}
                    </Link>
                  )}
              </p>
              <div className="mt-6 lg:w-[261px] lg:mx-auto">
                <Button
                  id="home-btn"
                  variant="primary"
                  type="button"
                  size="lg"
                  onClick={handleSignUpClick}
                >
                  <div className="flex items-center gap-2">
                    <div>
                      <img
                        src={CardColor}
                        alt="card icon"
                        className="w-4 h-4 object-contain"
                      />
                    </div>
                    <span>{t('homePage.signUpButton')}</span>
                  </div>
                </Button>
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleSignInClick}
                  className="font-semibold text-xs text-theme-primary underline bg-transparent border-none cursor-pointer"
                >
                  {t('homePage.alreadyHaveAccount')}
                </button>
              </div>
            </div>
          )}
          {/* 3 Steps - END */}

          {/* FEATURE SLIDER - START */}
          {featuredData.length > 0 && (
            <div className="feature-slider px-3 pt-6">
              <h2 className="text-sm font-extrabold text-theme-secondary text-left mt-4 mb-2">
                {t('offerDetails.otherOffers')}
              </h2>
              <div className="relative group">
                <button
                  onClick={() => {
                    const container = document.getElementById(
                      'feature-slider-container',
                    )
                    const cardWidth = 263 + 12
                    container?.scrollBy({ left: -cardWidth, behavior: 'smooth' })
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/10 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 -ml-3 hover:scale-110"
                >
                  <ChevronRight
                    size={20}
                    className="rotate-180 text-theme-primary"
                  />
                </button>

                <button
                  onClick={() => {
                    const container = document.getElementById(
                      'feature-slider-container',
                    )
                    const cardWidth = 263 + 12
                    container?.scrollBy({ left: cardWidth, behavior: 'smooth' })
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/10 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 -mr-3 hover:scale-110"
                >
                  <ChevronRight size={20} className="text-theme-primary" />
                </button>

                <div
                  id="feature-slider-container"
                  className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                >
                  <div
                    className="flex gap-3 pb-2"
                    style={{ width: 'max-content' }}
                  >
                    {featuredData.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleFeaturedOfferClick(item.id)}
                        className="bg-white rounded-[10px] shadow-md overflow-hidden lg:w-[296px] w-[180px] h-[257px] flex-shrink-0 transition-transform duration-300 ease-out hover:scale-[1.02] cursor-pointer"
                      >
                        <div className="overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-[117px] object-cover"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = require('../assets/images/backgrounds/featured-banner-1.png')
                            }}
                          />
                        </div>
                        <div className="px-2 py-2">
                          <div className="flex items-center">
                            <div className="w-[38px] h-[38px] border border-theme-secondary-foreground rounded-full flex items-center justify-center">
                              <img
                                src={item.icon}
                                alt={item.merchantName}
                                className="w-6 h-6 object-contain"
                                onError={(e) => {
                                  e.target.src = require('../assets/images/icons/featured-logo-1.png')
                                }}
                              />
                            </div>
                            <div className="text-[10px] font-semibold text-theme-sub-title ml-2">
                              {item.merchantName}
                            </div>
                          </div>
                          <p className="mt-3 font-extrabold text-xs text-theme-secondary text-left line-clamp-2">
                            {item.title}
                          </p>
                          {item.validUntil && (
                            <p className="mt-6 text-[10px] font-semibold text-theme-sub-title text-left">
                              {item.validUntil}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* FEATURE SLIDER - END */}

          <div className="pt-8 text-center">
            <Button
              id="home-btn"
              variant="secondary"
              size="lg"
              type="button"
              onClick={handleHomeClick}
              className="max-w-[261px]"
            >
              <div className="flex items-center gap-2">
                <div>
                  <HomeIcon className="w-[18px] h-[18px]" />
                </div>
                <span>{t('mainLayout.home')}</span>
              </div>
            </Button>
          </div>
        </div>

        {/* Offer Details - END */}
      </div>

      {/* Store Locations Modal */}
      <OfferLocationsModal
        open={isLocationModalOpen}
        onOpenChange={setIsLocationModalOpen}
        locations={offerLocations}
      />

      {/* NPS Feedback Modal */}
      {showNPS && (
        <FeedbackModal
          open={showNPS}
          onClose={() => setShowNPS(false)}
          triggerType="offer_engagement"
          channel="IN_APP"
          npsData={{
            viewsCount: viewedOffersRef.current.size,
            offerIds: Array.from(viewedOffersRef.current),
          }}
        />
      )}
    </>
  )
}

export default OfferDetails
